import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowRight, Target, Clock, Zap, GraduationCap, Briefcase, Rocket, Lightbulb, X } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardingModal({ onComplete }) {
  const { language } = useLanguage();
  const { getAuthHeaders } = useAuth();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ level: '', goal: '', time_available: '' });

  const steps = [
    {
      key: 'level',
      title_fr: 'Quel est votre niveau en IA ?',
      title_en: 'What is your AI level?',
      options: [
        { value: 'beginner', label_fr: 'Débutant', label_en: 'Beginner', desc_fr: 'Je découvre l\'IA', desc_en: 'I\'m discovering AI', icon: GraduationCap },
        { value: 'intermediate', label_fr: 'Intermédiaire', label_en: 'Intermediate', desc_fr: 'J\'utilise déjà quelques outils', desc_en: 'I already use some tools', icon: Zap },
        { value: 'advanced', label_fr: 'Avancé', label_en: 'Advanced', desc_fr: 'Je veux me perfectionner', desc_en: 'I want to level up', icon: Rocket },
      ],
    },
    {
      key: 'goal',
      title_fr: 'Quel est votre objectif ?',
      title_en: 'What is your goal?',
      options: [
        { value: 'curiosity', label_fr: 'Curiosite', label_en: 'Curiosity', desc_fr: 'Comprendre comment fonctionne l\'IA', desc_en: 'Understand how AI works', icon: Lightbulb },
        { value: 'productivity', label_fr: 'Productivite', label_en: 'Productivity', desc_fr: 'Gagner du temps au quotidien', desc_en: 'Save time daily', icon: Zap },
        { value: 'career', label_fr: 'Carriere', label_en: 'Career', desc_fr: 'Booster mon profil professionnel', desc_en: 'Boost my professional profile', icon: Target },
        { value: 'enterprise', label_fr: 'IA Expert', label_en: 'IA Expert', desc_fr: 'Former mon équipe à l\'IA', desc_en: 'Train my team in AI', icon: Briefcase },
      ],
    },
    {
      key: 'time_available',
      title_fr: 'Combien de temps avez-vous ?',
      title_en: 'How much time do you have?',
      options: [
        { value: '2h', label_fr: '2 heures', label_en: '2 hours', desc_fr: 'Initiation rapide', desc_en: 'Quick introduction', icon: Clock },
        { value: '4h', label_fr: '4 heures', label_en: '4 hours', desc_fr: 'Formation intermediate', desc_en: 'Intermediate training', icon: Clock },
        { value: '2x2h', label_fr: '2 x 2 heures', label_en: '2 x 2 hours', desc_fr: 'En 2 sessions', desc_en: 'In 2 sessions', icon: Clock },
        { value: '8h', label_fr: '8 heures', label_en: '8 hours', desc_fr: 'Formation complete expert', desc_en: 'Complete expert training', icon: Rocket },
      ],
    },
  ];

  const currentStep = steps[step];

  const handleSelect = (value) => {
    setAnswers({ ...answers, [currentStep.key]: value });
  };

  const handleNext = async () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      try {
        const headers = getAuthHeaders();
        const res = await axios.post(`${API}/onboarding`, answers, { headers, withCredentials: true });
        onComplete(res.data.recommended_program);
      } catch (err) {
        console.error(err);
        onComplete(null);
      }
    }
  };

  const isSelected = answers[currentStep.key];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="onboarding-modal">
      <div className="bg-white dark:bg-slate-900 rounded-2xl w-full max-w-lg p-8 shadow-2xl relative">
        <button onClick={() => onComplete(null)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" data-testid="onboarding-skip">
          <X className="w-5 h-5" />
        </button>

        {/* Progress */}
        <div className="flex gap-2 mb-8">
          {steps.map((_, i) => (
            <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? 'bg-blue-600' : 'bg-slate-200'}`} />
          ))}
        </div>

        {/* Logo */}
        <div className="flex items-center gap-2 mb-6">
          <img src="https://customer-assets.emergentagent.com/job_2071b760-1de1-4ab9-81d8-8ca2ec59ffcc/artifacts/lq8u7cy5_LOGO%20IA.png" alt="IA Academy" className="w-8 h-8 rounded-lg" />
          <span className="text-xs text-slate-400 tracking-wider font-medium">IA Academy</span>
        </div>

        <h2 className="text-2xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Poppins' }} data-testid="onboarding-question">
          {language === 'fr' ? currentStep.title_fr : currentStep.title_en}
        </h2>

        <div className="space-y-3 mb-8">
          {currentStep.options.map((opt) => {
            const Icon = opt.icon;
            const selected = answers[currentStep.key] === opt.value;
            return (
              <button
                key={opt.value}
                onClick={() => handleSelect(opt.value)}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all ${
                  selected
                    ? 'border-blue-600 bg-blue-50 shadow-sm'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
                data-testid={`onboarding-option-${opt.value}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                  selected ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className={`font-semibold ${selected ? 'text-blue-900' : 'text-slate-800'}`}>
                    {language === 'fr' ? opt.label_fr : opt.label_en}
                  </p>
                  <p className="text-xs text-slate-500">
                    {language === 'fr' ? opt.desc_fr : opt.desc_en}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        <Button
          className="w-full bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98] transition-all font-semibold"
          disabled={!isSelected}
          onClick={handleNext}
          data-testid="onboarding-next-btn"
        >
          {step < steps.length - 1
            ? (language === 'fr' ? 'Suivant' : 'Next')
            : (language === 'fr' ? 'Voir mon programme recommande' : 'See my recommended program')
          }
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        <p className="text-xs text-slate-400 text-center mt-3">
          {language === 'fr' ? `Étape ${step + 1} sur ${steps.length}` : `Step ${step + 1} of ${steps.length}`}
        </p>
      </div>
    </div>
  );
}
